import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import {
  GlobeAltIcon,
  CogIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import Lottie from 'lottie-react';
import RecordingAnimation from './Recording.json';

const AuthLayout = ({ children, rightTitle, rightSubtitle }) => {
  const { currentLanguage, toggleLanguage } = useLanguage();
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setShowLangDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="fixed inset-0 overflow-y-auto bg-[#0B0E14] z-[9999]">
      {/* Floating Header - Removed icons per user request */}
      <div className="fixed top-0 left-0 right-0 h-20 px-8 flex items-center justify-end gap-4 z-[10000] pointer-events-none">
      </div>

      <div className="min-h-full w-full flex items-center justify-center p-4 sm:p-8 relative py-12 lg:py-20">

        {/* Decorative Background Layer */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] bg-primary-900/10 rounded-full mix-blend-screen filter blur-[120px] opacity-30"></div>
          <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-indigo-900/10 rounded-full mix-blend-screen filter blur-[120px] opacity-30"></div>
          <div className="absolute inset-0 opacity-[0.02]">
            <div className="w-full h-full bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px]"></div>
          </div>
        </div>

        {/* Main Glassmorphic Container (Dark) */}
        <div className="relative z-10 w-full max-w-[1080px] bg-[#161B22]/40 backdrop-blur-3xl rounded-[2.5rem] border border-white/5 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.6)] flex flex-col lg:flex-row min-h-[640px] overflow-hidden">

          {/* Left Section: Content/Form */}
          <div className="flex-1 flex flex-col p-8 sm:p-12 lg:p-14 z-10">
            {/* Mobile Animation Section - Moved Above Form */}
            <div className="lg:hidden mb-8 text-center">
              <div className="bg-primary-600/5 rounded-3xl p-4 border border-white/5">
                <Lottie
                  animationData={RecordingAnimation}
                  loop={true}
                  autoplay={true}
                  className="mx-auto"
                  style={{ width: 160, height: 160 }}
                />
              </div>
            </div>

            <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
              {children}
            </div>

            <div className="mt-8 text-xs text-gray-500 font-medium tracking-wide uppercase opacity-60">
              © {new Date().getFullYear()} Valoria AI • Premium Intelligence
            </div>
          </div>

          {/* Right Section: Hero Panel (Premium Dark) */}
          <div className="hidden lg:flex flex-1 p-5">
            <div className="w-full h-full bg-gradient-to-br from-primary-600/10 to-primary-900/30 rounded-[2rem] border border-white/5 relative overflow-hidden flex flex-col items-center justify-center text-center px-10">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary-500/5 via-transparent to-transparent opacity-50"></div>

              <div className="relative z-10 w-full flex flex-col items-center">
                <div className="mb-8">
                  <Lottie
                    animationData={RecordingAnimation}
                    loop={true}
                    autoplay={true}
                    style={{ width: 380, height: 380 }}
                    className="drop-shadow-[0_0_20px_rgba(79,70,229,0.2)]"
                  />
                </div>

                <div className="max-w-xs px-2">
                  <h1 className="text-2xl font-bold text-white mb-2 tracking-tight">
                    {rightTitle || 'Welcome to Valoria'}
                  </h1>
                  <p className="text-sm text-gray-400 font-medium leading-relaxed opacity-80">
                    {rightSubtitle ||
                      'Empower your business with state-of-the-art voice AI and seamless automation.'}
                  </p>
                </div>
              </div>

              {/* Corner Accents */}
              <div className="absolute top-8 right-8 opacity-10">
                <div className="w-12 h-12 border-t border-r border-white rounded-tr-xl"></div>
              </div>
              <div className="absolute bottom-8 left-8 opacity-10">
                <div className="w-12 h-12 border-b border-l border-white rounded-bl-xl"></div>
              </div>
            </div>
          </div>


        </div>
      </div>
    </div>
  );
};

export default AuthLayout;